import {
  CollisionChangedEvent,
  HighlightednessEvent,
  PlacementStageChangedEvent,
  PreviewHoverEvent,
  SelectionEvent,
  VisibilityEvent,
} from 'src/app/configurator/product/interfaces/ProductEvents';
import { Box3, Vector3 } from 'three';
import {
  ProductColisionEnum,
  ProductHighlightednessEnum,
  ProductPlacementStageEnum,
  ProductSelectionENUM,
  ProductTypeENUM,
  ProductVisibilityENUM,
} from '../../enums/ProductEnums';
import { ProductDTO } from '../DTOs/ProductDTO';
import { ModuleProductState } from './ModuleProductState';
import { Product, ProductEventMap, ProductEventsENUM } from './Product';
import { ProductBlueprint } from './ProductBlueprint';
import { Wall } from './Wall';

export enum StatefulProductEventsENUM {
  RotateEvent = ProductEventsENUM.RotateEvent,
  SelectionChanged = 'SelectionChanged',
  HighlightednessChanged = 'HighlightednessChanged',
  PlacementStageChanged = 'PlacementStageChanged',
  ColisionChanged = 'ColisionChanged',
  PreviewHover = 'PreviewHover',
}

export interface SelectionData {
  selection: ProductSelectionENUM;
  localIntersectionPoint?: Vector3;
  reselected: boolean;
}

export interface ModuleProductEventMap extends ProductEventMap {
  SelectionChanged: SelectionEvent;
  HighlightednessChanged: HighlightednessEvent;
  PlacementStageChanged: PlacementStageChangedEvent;
  ColisionChanged: CollisionChangedEvent;
  PreviewHover: PreviewHoverEvent;
}

export class ModuleProduct extends Product<ModuleProductEventMap> {
  public static override fromProductBlueprint(productBlueprint: ProductBlueprint, type?: ProductTypeENUM) {
    return new ModuleProduct(undefined, productBlueprint, type);
  }

  public static override fromDTO(
    productDTO: ProductDTO,
    availibleBlueprints: ProductBlueprint[],
    placementStage: ProductPlacementStageEnum | undefined = undefined,
  ) {
    const foundProductBlueprint = availibleBlueprints.find(
      (blueprint) => blueprint.id === productDTO.productBlueprintId?.toString(),
    );

    if (!foundProductBlueprint) {
      throw new Error('Products productBlueprint cannot be null');
    }

    const product = new ModuleProduct(
      productDTO.id?.toString(),
      foundProductBlueprint,
      productDTO.productType,
      ProductVisibilityENUM.Visible,
      undefined,
      placementStage,
      productDTO.wallId,

      productDTO.transformationData.rotation,
    );

    if (productDTO.transformationData?.position) {
      product.position.set(
        productDTO.transformationData.position[0],
        productDTO.transformationData.position[1],
        productDTO.transformationData.position[2],
      );
    }

    if (productDTO.transformationData?.rotation) {
      product.rotation.set(
        productDTO.transformationData.rotation[0],
        productDTO.transformationData.rotation[1],
        productDTO.transformationData.rotation[2],
      );
    }

    return product;
  }

  protected override _productState: ModuleProductState;

  constructor(
    id: string | undefined,
    productBlueprint: ProductBlueprint,
    type: ProductTypeENUM = ProductTypeENUM.Module,
    visibility: ProductVisibilityENUM = ProductVisibilityENUM.Invisible,
    selection: ProductSelectionENUM = ProductSelectionENUM.Unselected,
    placementStage: ProductPlacementStageEnum = ProductPlacementStageEnum.PreAdding,
    wallId?: string,
    rotation = [0, 0, 0],
  ) {
    super(id, productBlueprint, type, wallId, rotation);
    this._productState = new ModuleProductState(selection, undefined, placementStage, undefined, visibility);
    if (visibility === ProductVisibilityENUM.Invisible) {
      this.visible = false;
    }
    this._checkLoadingStage();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public override addEventListener(type: string, callback: any): void {
    super.addEventListener(type, callback);
    // Add any specific logic for StatefulProduct here
    // For instance, handling specific cases for SelectionChanged or VisibilityChanged
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public override removeEventListener(type: string, callback: any): void {
    super.removeEventListener(type, callback);
    // Similarly, add any specific logic for StatefulProduct here
  }

  override findWall(walls: Wall[]) {
    super.findWall(walls);

    if (this.wall) {
      if (this.wall.enableObjectVisibility) {
        this.visibility = ProductVisibilityENUM.Visible;
      } else {
        this.visibility = this.wall.visible ? ProductVisibilityENUM.Visible : ProductVisibilityENUM.Invisible;
      }
    }
  }

  public get selection() {
    return this._productState.selection;
  }

  public setSelection(selection: ProductSelectionENUM, localPoint?: Vector3) {
    if (this._productState.selection !== selection) {
      const reselected = this._productState.selection === selection && selection === ProductSelectionENUM.Selected;
      this._productState.selection = selection;

      this.dispatchProductEvent(StatefulProductEventsENUM.SelectionChanged, {
        data: { selection: selection, localIntersectionPoint: localPoint, reselected: reselected },
      } as SelectionEvent);
    }
  }

  public get highlightedness() {
    return this._productState.highlightedness;
  }

  public sethighlightedness(highlightedness: ProductHighlightednessEnum, color = 0xffffff) {
    if (this._productState.highlightedness !== highlightedness) {
      this._productState.highlightedness = highlightedness;
      this.dispatchProductEvent(StatefulProductEventsENUM.HighlightednessChanged, {
        data: { highlightedness: highlightedness, color: color },
      } as HighlightednessEvent);
    }
  }

  public get placementStage() {
    return this._productState.placementStage;
  }

  public set placementStage(placementStage: ProductPlacementStageEnum) {
    this.setPlacementStage(placementStage, this);
    for (const child of this.children) {
      if (child instanceof ModuleProduct) {
        child.setPlacementStage(placementStage, this);
      }
    }
  }

  public setPlacementStage(placementStage: ProductPlacementStageEnum, originOfPlacementStageChange: Product) {
    if (this._productState.placementStage !== placementStage) {
      this._productState.placementStage = placementStage;

      if (
        placementStage !== ProductPlacementStageEnum.PreAdding &&
        this.visibility === ProductVisibilityENUM.Invisible
      ) {
        this.visibility = ProductVisibilityENUM.Visible;
      }

      this.dispatchProductEvent(StatefulProductEventsENUM.PlacementStageChanged, {
        data: { placementStage: placementStage, originOfPlacementStageChange: originOfPlacementStageChange },
      } as PlacementStageChangedEvent);
    }
  }

  public get colision() {
    return this._productState.colision;
  }

  public set colision(colision: ProductColisionEnum) {
    if (this._productState.colision !== colision) {
      this._productState.colision = colision;
      this.dispatchProductEvent(StatefulProductEventsENUM.ColisionChanged, { data: colision } as CollisionChangedEvent);
    }
  }

  public override get boundingBox() {
    if (this.visibility === ProductVisibilityENUM.Visible) {
      return super.boundingBox;
    } else {
      return new Box3();
    }
  }

  public override set boundingBox(value) {
    super.boundingBox = value;
  }

  public previewHovered(previewProduct: Product) {
    this.dispatchProductEvent(StatefulProductEventsENUM.PreviewHover, {
      data: previewProduct,
    } as PreviewHoverEvent);
  }
}
